import React, { useState, memo } from "react";
import useTranslation from "next-translate/useTranslation";
import styles from "./GameButtons.module.css";
import { useGamePlay, offerDrawResponse } from "../../../redux/gamePlay";
import Popup from "../../../components/ui/popup";
import { GamePlayWs } from "../../../services/wsOutRoutes";
import { Button } from "@/components/ui/button";
import { FaComments, FaFlag, FaHandshake, FaTrashAlt } from "react-icons/fa";

function sendResign() {
  GamePlayWs.sendResign();
}

function sendOfferDraw() {
  GamePlayWs.sendOfferDraw();
}

function sendAbort() {
  GamePlayWs.sendAbort();
}

function sendOfferDrawResponse(accept: boolean) {
  GamePlayWs.offerDrawResponse(accept);
  offerDrawResponse();
}

function GameButtonContent({
  icon,
  label,
  compactFallback = false,
}: {
  icon: React.ReactNode;
  label: string;
  compactFallback?: boolean;
}) {
  const labelClassName = compactFallback
    ? `${styles.compactButtonLabel} text-[11px] sm:text-xs`
    : "min-w-0 truncate text-xs";

  return (
    <>
      <span
        aria-hidden="true"
        className="flex items-center justify-center w-4 h-4 text-sm shrink-0 opacity-85"
      >
        {icon}
      </span>
      <span className={labelClassName}>{label}</span>
    </>
  );
}

export default function GameButtons({
  isDesktop,
  setChatOpened,
  handleShowChatWindow,
  unreadChatMessages,
}) {
  const { t } = useTranslation("common");

  const { offerDrawReceived, showAbort } = useGamePlay();

  const [offerDrawStart, setOfferDrawStart] = useState(false);
  const [resignStart, setResignStart] = useState(false);
  const [abortStart, setAbortStart] = useState(false);
  const [offerDrawSent, setOfferDrawSent] = useState(false);

  function doSendOfferDraw() {
    setOfferDrawStart(false);
    setOfferDrawSent(true);
    sendOfferDraw();
  }

  function cancel() {
    setOfferDrawStart(false);
    setResignStart(false);
    setAbortStart(false);
  }

  function renderButtons(
    label1: string,
    action1: () => void,
    label2: string = null,
    action2: () => void = null,
    isDesktop: boolean,
  ) {
    return (
      <div className="flex justify-center space-x-2">
        <Button
          size={isDesktop ? "full" : "wide"}
          variant="outline"
          onClick={action1}
        >
          {label1}
        </Button>
        {label2 && action2 && (
          <Button size={isDesktop ? "full" : "wide"} onClick={action2}>
            {label2}
          </Button>
        )}
      </div>
    );
  }

  function renderInfo(notPopup: boolean, isDesktop: boolean) {
    if (offerDrawReceived) {
      return (
        <div className={styles.gameInfo}>
          <div className={styles.gameInfoTitle}>
            {t("Your opponent has offered a draw Do you accept?")}
          </div>
          {renderButtons(
            t("Cancel"),
            () => sendOfferDrawResponse(false),
            t("Yes"),
            () => sendOfferDrawResponse(true),
            isDesktop,
          )}
        </div>
      );
    } else if (offerDrawStart) {
      return (
        <div className={styles.gameInfo}>
          {notPopup && (
            <div className="pb-2 font-semibold">{t("Offer draw")}</div>
          )}
          <div className={styles.gameInfoTitle}>{t("Are you sure?")}</div>
          {renderButtons(
            t("Cancel"),
            cancel,
            t("Yes"),
            doSendOfferDraw,
            isDesktop,
          )}
        </div>
      );
    } else if (resignStart) {
      return (
        <div className={styles.gameInfo}>
          {notPopup && <div className="pb-2 font-semibold">{t("Resign")}</div>}
          <div className={styles.gameInfoTitle}>{t("Are you sure?")}</div>
          {renderButtons(t("Cancel"), cancel, t("Yes"), sendResign, isDesktop)}
        </div>
      );
    } else if (abortStart) {
      return (
        <div className={styles.gameInfo}>
          {notPopup && (
            <div className="pb-2 font-semibold">{t("Abort game")}</div>
          )}
          <div className={styles.gameInfoTitle}>{t("Are you sure?")}</div>
          {renderButtons(t("Cancel"), cancel, t("Yes"), sendAbort, isDesktop)}
        </div>
      );
    } else if (offerDrawSent) {
      return (
        <div className={styles.gameInfo}>
          <div className={styles.gameInfoTitle}>
            {t("You have offered a draw")}
          </div>
          <button
            className="w-20 btn btn-sm btn-outline"
            onClick={() => setOfferDrawSent(false)}
          >
            {t("Close")}
          </button>
        </div>
      );
    }
  }

  function renderPopup() {
    let title: string;
    if (offerDrawStart || offerDrawSent) {
      title = t("Offer draw");
    } else if (offerDrawReceived) {
      title = t("Draw");
    } else if (resignStart) {
      title = t("Resign");
    } else if (abortStart) {
      title = t("Abort game");
    }

    if (title) {
      return (
        <Popup title={title} onClose={cancel}>
          {renderInfo(false, false)}
        </Popup>
      );
    }
  }

  function renderDesktopButtons() {
    if (abortStart || resignStart || offerDrawStart || offerDrawSent) {
      return renderInfo(true, true);
    }

    return (
      <>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => setOfferDrawStart(true)}
            variant="outline"
            size="full"
            className="min-w-0 gap-2 px-3"
            aria-label={t("Offer draw")}
          >
            <GameButtonContent
              icon={<FaHandshake className="w-4 h-4" />}
              label={t("Draw")}
            />
          </Button>
          <Button
            onClick={() => setResignStart(true)}
            variant="outline"
            size="full"
            className="min-w-0 gap-2 px-3"
            aria-label={t("Resign")}
          >
            <GameButtonContent
              icon={<FaFlag className="w-4 h-4" />}
              label={t("Resign")}
            />
          </Button>
        </div>
        {showAbort && (
          <div className="flex justify-center pt-2 mt-4 border-t border-gray-200 md:justify-start">
            <Button
              onClick={() => setAbortStart(true)}
              variant="ghost"
              size="sm"
              className="min-w-0 gap-2 text-xs"
              aria-label={t("Abort game")}
            >
              {/* <GameButtonContent
                icon={<FaTrashAlt className="h-3.5 w-3.5" />}
                label={t("Abort game")}
              /> */}
              {t("Abort game")}
            </Button>
          </div>
        )}

        {renderInfo(true, true)}
      </>
    );
  }

  function renderMobileButtons() {
    return (
      <>
        <div
          className={`${
            offerDrawSent
              ? "hidden"
              : "flex w-full justify-center space-x-3 items-center"
          }`}
        >
          <Button
            variant="outline"
            size="full"
            onClick={() => setOfferDrawStart(true)}
            className="min-w-0 gap-1 px-2 sm:gap-2"
            aria-label={t("Offer draw")}
          >
            <GameButtonContent
              icon={<FaHandshake className="w-4 h-4" />}
              label={t("Draw")}
              compactFallback
            />
          </Button>
          <Button
            variant="outline"
            size="full"
            onClick={() => setResignStart(true)}
            className="min-w-0 gap-1 px-2 sm:gap-2"
            aria-label={t("Resign")}
          >
            <GameButtonContent
              icon={<FaFlag className="w-3 h-3" />}
              label={t("Resign")}
              compactFallback
            />
          </Button>
          <Button
            variant="outline"
            size="full"
            onClick={() => {
              setChatOpened(true);
              handleShowChatWindow(true);
            }}
            badgeNum={unreadChatMessages}
            className="min-w-0 gap-1 px-2 sm:gap-2"
            aria-label={t("Chat")}
          >
            <GameButtonContent
              icon={<FaComments className="w-4 h-4" />}
              label={t("Chat")}
              compactFallback
            />
          </Button>
        </div>
        {showAbort && (
          <div className="flex justify-center pt-2 mt-4 border-t border-gray-200">
            <Button
              onClick={() => setAbortStart(true)}
              variant="ghost"
              size="sm"
              className="min-w-0 gap-2 text-xs"
              aria-label={t("Abort game")}
            >
              {/* <GameButtonContent
                icon={<FaTrashAlt className="h-3.5 w-3.5" />}
                label={t("Abort game")}
              /> */}
              {t("Abort game")}
            </Button>
          </div>
        )}
        {renderPopup()}
      </>
    );
  }

  return (
    <div className="w-full px-4 py-1">
      {isDesktop ? renderDesktopButtons() : renderMobileButtons()}
    </div>
  );
}
